import { cva, type VariantProps } from "class-variance-authority"
import { clsx } from "clsx"

/*
* cva maps the Limitless size scale and the boolean modifiers onto `.table-*` classes.
*/
const tableVariants = cva("table", {
  variants: {
    size: {
      xs: "table-xs",
      sm: "table-sm",
      default: "",
      lg: "table-lg",
      xlg: "table-xlg",
    },
    striped: {
      true: "table-striped",
    },
    hover: {
      true: "table-hover",
    },
    bordered: {
      true: "table-bordered",
    },
  },
  defaultVariants: {
    size: "default",
    striped: false,
    hover: false,
    bordered: false,
  },
})

type TableSize = NonNullable<VariantProps<typeof tableVariants>["size"]>


function TableRoot({
  className,
  containerClassName,
  scrollHeight,
  responsive = true,
  size = "default",
  striped = false,
  hover = false,
  bordered = false,
  ...props
}: React.ComponentProps<"table"> &
  VariantProps<typeof tableVariants> & {
    /** Wrap the table in Bootstrap's `.table-responsive` scroll container. Defaults to `true`. */
    responsive?: boolean
    /** Extra classes for the `.table-responsive` container. Ignored when `responsive` is `false`. */
    containerClassName?: string
    /**
     * Makes the scroll container a box of this height, so the rows scroll inside it instead of the
     * page growing. Any CSS length; a number is pixels. Ignored when `responsive` is `false` —
     * that drops the container this applies to. A caller-chosen dimension cannot come from a
     * class, so it is applied inline.
     */
    scrollHeight?: number | string
  }) {
  const table = (
    <table
      data-slot="table"
      className={clsx(tableVariants({ size, striped, hover, bordered }), className)}
      {...props}
    />
  )

  if (!responsive) {
    return table
  }

  return (
    <div
      data-slot="table-container"
      className={clsx("table-responsive", containerClassName)}
      style={scrollHeight != null ? {height: scrollHeight, overflowY: "auto"} : undefined}
    >
      {table}
    </div>
  )
}

function TableHeader(props: React.ComponentProps<"thead">) {
  return <thead data-slot="table-header" {...props} />
}

function TableBody(props: React.ComponentProps<"tbody">) {
  return <tbody data-slot="table-body" {...props} />
}

function TableFooter(props: React.ComponentProps<"tfoot">) {
  return <tfoot data-slot="table-footer" {...props} />
}

function TableRow(props: React.ComponentProps<"tr">) {
  return <tr data-slot="table-row" {...props} />
}

function TableHead(props: React.ComponentProps<"th">) {
  return <th data-slot="table-head" {...props} />
}

function TableCell(props: React.ComponentProps<"td">) {
  return <td data-slot="table-cell" {...props} />
}

function TableCaption(props: React.ComponentProps<"caption">) {
  return <caption data-slot="table-caption" {...props} />
}

const Table = {
  Root: TableRoot,
  Header: TableHeader,
  Body: TableBody,
  Footer: TableFooter,
  Row: TableRow,
  Head: TableHead,
  Cell: TableCell,
  Caption: TableCaption,
}

export { Table }

export type { TableSize }
